import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { Link } from 'react-router-dom'
import Header from '../components/Header/header'
import SubscribeSection from '../components/Subscribe'
import FooterSection from '../components/Footer'
import firebase from '../api/firestore'

const db = firebase.firestore()
const googleProvider = new firebase.auth.GoogleAuthProvider()

export default function SubmitItineraryPage() {
  const emptyForm = { name: '', googleMapsUrl: '', description: '' }

  const [user, setUser] = useState(null)
  const [authLoading, setAuthLoading] = useState(true)
  const [formData, setFormData] = useState(emptyForm)
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingItems, setLoadingItems] = useState(true)
  const [message, setMessage] = useState('')
  const [urlError, setUrlError] = useState('')
  const [tooltipOpen, setTooltipOpen] = useState(false)

  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged((u) => {
      setUser(u)
      setAuthLoading(false)
    })
    return unsubscribe
  }, [])

  useEffect(() => {
    fetchApprovedItineraries()
  }, [])

  const fetchApprovedItineraries = async () => {
    setLoadingItems(true)
    try {
      const snapshot = await db
        .collection('itineraries')
        .where('approved', '==', true)
        .orderBy('createdAt', 'desc')
        .get()
      setItems(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })))
    } catch (err) {
      console.error('Error fetching itineraries:', err)
    }
    setLoadingItems(false)
  }

  const signInWithGoogle = () => {
    firebase.auth().signInWithPopup(googleProvider).catch((err) => {
      console.error('Sign-in error:', err)
      setMessage('Sign-in failed. Please try again.')
    })
  }

  const signOut = () => firebase.auth().signOut()

  const isValidGoogleMapsUrl = (url) =>
    /^https:\/\/maps\.app\.goo\.gl\/.+/.test(url.trim())

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setMessage('')

    const name = formData.name.trim().slice(0, 80)
    const googleMapsUrl = formData.googleMapsUrl.trim().slice(0, 200)
    const description = formData.description.trim().slice(0, 500)

    if (!name) {
      setMessage('Please add a name.')
      return
    }

    if (!googleMapsUrl) {
      setMessage('Please paste your Google Maps link.')
      return
    }

    if (!isValidGoogleMapsUrl(googleMapsUrl)) {
      setMessage('Link must start with https://maps.app.goo.gl/')
      return
    }

    setLoading(true)

    try {
      const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000)
      const recentSnapshot = await db
        .collection('itineraries')
        .where('submittedBy.uid', '==', user.uid)
        .where('createdAt', '>=', oneDayAgo)
        .get()

      if (recentSnapshot.size >= 10) {
        setMessage("You've reached the 10 submissions per day limit. Try again tomorrow.")
        setLoading(false)
        return
      }

      await db.collection('itineraries').add({
        name,
        googleMapsUrl,
        description,
        approved: false,
        voteCount: 0,
        submittedBy: {
          uid: user.uid,
          displayName: user.displayName || '',
          email: user.email || ''
        },
        createdAt: firebase.firestore.FieldValue.serverTimestamp()
      })
      setMessage('Thanks! Your itinerary was submitted for review.')
      setFormData(emptyForm)
    } catch (err) {
      console.error('Error submitting itinerary:', err)
      setMessage('Something went wrong. Please try again.')
    }

    setLoading(false)
  }

  const handleUpvote = async (id, currentVotes) => {
    const votedKey = `exploroff-voted-${id}`
    if (localStorage.getItem(votedKey)) {
      setMessage('You already upvoted this itinerary.')
      return
    }

    try {
      await db.collection('itineraries').doc(id).update({
        voteCount: (currentVotes || 0) + 1
      })
      localStorage.setItem(votedKey, 'true')
      setItems((prev) =>
        prev.map((item) =>
          item.id === id
            ? { ...item, voteCount: (item.voteCount || 0) + 1 }
            : item
        )
      )
      setMessage('')
    } catch (err) {
      console.error('Error upvoting:', err)
      setMessage('Could not upvote. Please try again.')
    }
  }

  return (
    <>
      <Helmet>
        <title>Submit Your Itinerary | ExplorOFF</title>
        <meta
          name="description"
          content="Submit your favorite outdoor itinerary to the ExplorOFF community."
        />
      </Helmet>

      <Header />

      <div className="exploroff-submit-page">
        <div className="container">
          <div className="exploroff-submit-card">
            <h1 className="exploroff-submit-title">Submit Your Itinerary</h1>
            <p className="exploroff-submit-subtitle">
              Share a place from Google Maps with the ExplorOFF community.
            </p>

            {authLoading ? null : !user ? (
              <div className="exploroff-auth-section">
                <p className="exploroff-auth-prompt">
                  Sign in to submit an itinerary.
                </p>
                <button
                  type="button"
                  className="exploroff-google-btn"
                  onClick={signInWithGoogle}
                >
                  <svg className="exploroff-google-icon" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Sign in with Google
                </button>
              </div>
            ) : (
              <>
                <div className="exploroff-user-bar">
                  <Link to={`/user/${user.uid}`} className="exploroff-user-info exploroff-user-info-link">
                    <div className="exploroff-user-avatar-fallback">
                      {(user.displayName || user.email || '?')[0].toUpperCase()}
                    </div>
                    <div className="exploroff-user-details">
                      <span className="exploroff-user-name">
                        {user.displayName || user.email}
                      </span>
                      <span className="exploroff-user-label">View profile</span>
                    </div>
                  </Link>
                  <div className="exploroff-user-bar-actions">
                    <Link to="/profile/edit" className="exploroff-edit-profile-btn exploroff-edit-profile-btn-sm">
                      Edit profile
                    </Link>
                    <button
                      type="button"
                      className="exploroff-signout-link"
                      onClick={signOut}
                    >
                      Sign out
                    </button>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="exploroff-submit-form">
                  <div className="exploroff-submit-group">
                    <label className="exploroff-submit-label" htmlFor="name">
                      Name your itinerary
                    </label>
                    <input
                      id="name"
                      className="exploroff-submit-input"
                      type="text"
                      name="name"
                      placeholder="Hidden gems in Iceland"
                      value={formData.name}
                      onChange={handleChange}
                      maxLength={80}
                      required
                    />
                  </div>

                  <div className="exploroff-submit-group">
                    <label className="exploroff-submit-label" htmlFor="googleMapsUrl">
                      Google Maps saved list link
                      <span className="exploroff-tooltip-wrap">
                        <span
                          className="exploroff-tooltip-icon"
                          onClick={() => setTooltipOpen(!tooltipOpen)}
                        >?</span>
                        <span className={`exploroff-tooltip-box${tooltipOpen ? ' exploroff-tooltip-visible' : ''}`}>
                          A Google Maps saved list is a collection of places you've saved in Google Maps.
                          To get the link: open Google Maps → Your lists → select a list → tap Share → copy the link.
                        </span>
                      </span>
                    </label>
                    <input
                      id="googleMapsUrl"
                      className="exploroff-submit-input"
                      type="url"
                      name="googleMapsUrl"
                      placeholder="https://maps.app.goo.gl/..."
                      value={formData.googleMapsUrl}
                      maxLength={200}
                      onChange={(e) => {
                        handleChange(e)
                        const val = e.target.value.trim()
                        if (val && !isValidGoogleMapsUrl(val)) {
                          setUrlError('Link must start with https://maps.app.goo.gl/')
                        } else {
                          setUrlError('')
                        }
                      }}
                      pattern="https://maps\.app\.goo\.gl/.*"
                      required
                    />
                    {urlError ? (
                      <p className="exploroff-field-error">{urlError}</p>
                    ) : null}
                  </div>

                  <div className="exploroff-submit-group">
                    <label className="exploroff-submit-label" htmlFor="description">
                      Description
                    </label>
                    <textarea
                      id="description"
                      className="exploroff-submit-textarea"
                      name="description"
                      placeholder="Why is this place worth sharing?"
                      value={formData.description}
                      onChange={handleChange}
                      maxLength={500}
                    />
                  </div>

                  <div className="exploroff-submit-actions">
                    <button
                      type="submit"
                      className="exploroff-submit-button"
                      disabled={loading}
                    >
                      <span>{loading ? 'Submitting...' : 'Submit'}</span>
                    </button>
                  </div>

                  {message ? (
                    <p className="exploroff-submit-message">{message}</p>
                  ) : null}
                </form>
              </>
            )}
          </div>

          <div className="exploroff-feed-section">
            <h2 className="exploroff-feed-title">Community Itineraries</h2>

            {loadingItems ? (
              <p className="exploroff-feed-loading">Loading itineraries...</p>
            ) : items.length === 0 ? (
              <p className="exploroff-feed-loading">No approved itineraries yet.</p>
            ) : (
              <div className="exploroff-feed-grid">
                {items.map((item) => (
                  <div key={item.id} className="exploroff-feed-card">
                    <div className="exploroff-feed-card-header">
                      <div className="exploroff-feed-card-main">
                        <Link to={`/itinerary/${item.id}`} className="exploroff-feed-card-title-link">
                          <h3 className="exploroff-feed-card-title">
                            {item.name || 'Itinerary'}
                          </h3>
                        </Link>
                        <p className="exploroff-feed-card-date">
                          {item.submittedBy?.displayName ? (
                            <>
                              <Link to={`/user/${item.submittedBy.uid}`} className="exploroff-feed-user-link">
                                {item.submittedBy.displayName}
                              </Link>
                              {' · '}
                            </>
                          ) : null}
                          {item.createdAt
                            ? new Date(item.createdAt.toDate()).toLocaleDateString()
                            : ''}
                        </p>
                      </div>
                      <button
                        type="button"
                        className="exploroff-feed-vote-button"
                        onClick={() => handleUpvote(item.id, item.voteCount)}
                      >
                        👍 {item.voteCount || 0}
                      </button>
                    </div>

                    {item.description ? (
                      <p className="exploroff-feed-card-description">
                        {item.description}
                      </p>
                    ) : null}

                    <a
                      href={item.googleMapsUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="exploroff-feed-map-link"
                    >
                      Open Map
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <SubscribeSection />
      <FooterSection />
    </>
  )
}
